import React, { useState } from 'react';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Zoom from '@material-ui/core/Zoom';
import HelpOutlineOutlinedIcon from '@material-ui/icons/HelpOutlineOutlined';
import { colors } from '@skyslope/mache';
import type SignatureCanvas from 'react-canvas-draw';
// @ts-expect-error -- This code was written prior to enforcing lint rules
import trimCanvas from 'trim-canvas';
import type { ISignatureSettings, ISigner } from '../../store/types';
import { CanvasWrapper } from './CanvasWrapper';

const useStyles = makeStyles(() => ({
  drawingSection: {
    '&:last-child': {
      marginTop: 36,
    },
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  tooltipHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  canvas: {
    backgroundColor: 'white',
    height: 170,
    width: '100%',
  },
  image: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'white',
    height: 170,
    width: '100%',
  },
  tooltip: {
    padding: '30px',
    maxWidth: '250px',
    backgroundColor: colors.grey[900],
    borderRadius: '0px',
    fontSize: '17px',
  },
  arrow: {
    color: colors.grey[900],
  },
}));

interface IProps {
  signatureSettings: ISignatureSettings;
  setSignatureSettings: (signatureSettings: ISignatureSettings) => void;
  signer: ISigner;
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const DrawSignature = (props: IProps) => {
  const signatureRef = React.useRef<SignatureCanvas>(null);
  const initialsRef = React.useRef<SignatureCanvas>(null);
  const [hasInitials, setHasInitials] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [maxWidth, setMaxWidth] = useState(0);
  const classes = useStyles();
  const originalSignatureSettingNotChanged =
    props.signer?.signingSettings?.signatureType === 'image' &&
    props.signer?.signingSettings?.signatureValue === props.signatureSettings?.signatureValue;
  const originalInitialsSettingNotChanged =
    props.signer?.signingSettings?.signatureType === 'image' &&
    props.signer?.signingSettings?.initialsValue === props.signatureSettings?.initialsValue;

  React.useEffect(() => {
    let signValues: Partial<ISignatureSettings> = {};
    if (props.signer?.signingSettings?.signatureValue && props.signer?.signingSettings?.signatureType === 'image') {
      setHasSignature(true);
      signValues.signatureValue = props.signer?.signingSettings?.signatureValue;
    }
    if (props.signer?.signingSettings?.initialsValue && props.signer?.signingSettings?.signatureType === 'image') {
      setHasInitials(true);
      signValues.initialsValue = props.signer?.signingSettings?.initialsValue;
    }
    const valuesToUpdate = Object.keys(signValues).length;
    if (valuesToUpdate > 0) {
      props.setSignatureSettings({
        ...props.signatureSettings,
        initialsValue: signValues.initialsValue || props.signatureSettings.initialsValue,
        signatureValue: signValues.signatureValue || props.signatureSettings.signatureValue,
        signatureType: 'image',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const clearCanvas = (sigCanvas: SignatureCanvas | 'image', keyName: 'signatureValue' | 'initialsValue') => {
    if (keyName === 'signatureValue') {
      setHasSignature(false);
    } else {
      setHasInitials(false);
    }
    if (sigCanvas !== 'image') {
      sigCanvas.clear();
    }
    props.setSignatureSettings({ ...props.signatureSettings, [keyName]: '' });
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const updateValue = (signCanvas: SignatureCanvas, keyName: 'signatureValue' | 'initialsValue') => {
    if (keyName === 'signatureValue') {
      setHasSignature(true);
    } else {
      setHasInitials(true);
    }
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const drawing = signCanvas.canvas.drawing;
    let canvas = document.createElement('canvas');
    canvas.width = drawing.width;
    canvas.height = drawing.height;
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    canvas.getContext('2d').drawImage(drawing, 0, 0);
    const trimmedCanvas = trimCanvas(canvas);

    const base64 = trimmedCanvas.toDataURL();
    props.setSignatureSettings({
      ...props.signatureSettings,
      [keyName]: base64,
      signatureType: 'image',
      signatureFont: '',
    });
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleSetMaxWidth = (width: number) => {
    if (width > maxWidth) {
      setMaxWidth(width);
    }
  };
  return (
    <>
      <div className={classes.drawingSection}>
        <div className={classes.sectionHeader}>
          <Typography className={classes.tooltipHeader} variant="body2">
            {originalSignatureSettingNotChanged ? 'Your saved signature' : 'Draw your signature'}
            {originalSignatureSettingNotChanged && (
              <Tooltip
                title={
                  <div>
                    To create a new saved signature,
                    <br />
                    please edit the existing one and draw a new one.
                  </div>
                }
                placement="bottom-start"
                TransitionComponent={Zoom}
                arrow
                classes={{
                  tooltip: classes.tooltip,
                  arrow: classes.arrow,
                }}
              >
                <HelpOutlineOutlinedIcon
                  style={{
                    color: colors.grey[700],
                    fontSize: '18px',
                    marginLeft: '16px',
                  }}
                />
              </Tooltip>
            )}
          </Typography>
          <Button
            variant="text"
            color="primary"
            disabled={!hasSignature}
            onClick={() =>
              clearCanvas(originalSignatureSettingNotChanged ? 'image' : signatureRef.current!, 'signatureValue')
            }
          >
            {originalSignatureSettingNotChanged ? 'Edit' : 'Clear'}
          </Button>
        </div>
        {originalSignatureSettingNotChanged ? (
          <div className={classes.image}>
            <img src={props.signer?.signingSettings?.signatureValue} alt="Saved signature" />
          </div>
        ) : (
          <CanvasWrapper
            ref={signatureRef}
            onEnd={() => updateValue(signatureRef.current!, 'signatureValue')}
            canvasProps={{ className: classes.canvas }}
            data-spec="signature-canvas"
            hasContent={hasSignature}
            key={0}
            canvasWidth={maxWidth}
            setMaxWidth={handleSetMaxWidth}
          />
        )}
      </div>
      <div className={classes.drawingSection}>
        <div className={classes.sectionHeader}>
          <Typography className={classes.tooltipHeader} variant="body2">
            {originalInitialsSettingNotChanged ? 'Your saved initials' : 'Draw your initials'}
            {originalInitialsSettingNotChanged && (
              <Tooltip
                title={
                  <div style={{ maxWidth: '220px' }}>
                    To create a new saved initial, please edit the existing one
                    <br />
                    and draw a new one.
                  </div>
                }
                placement="bottom-start"
                TransitionComponent={Zoom}
                arrow
                classes={{
                  tooltip: classes.tooltip,
                  arrow: classes.arrow,
                }}
              >
                <HelpOutlineOutlinedIcon
                  style={{
                    color: colors.grey[700],
                    fontSize: '18px',
                    marginLeft: '16px',
                  }}
                />
              </Tooltip>
            )}
          </Typography>
          <Button
            variant="text"
            color="primary"
            disabled={!hasInitials}
            onClick={() =>
              clearCanvas(originalInitialsSettingNotChanged ? 'image' : initialsRef.current!, 'initialsValue')
            }
          >
            {originalInitialsSettingNotChanged ? 'Edit' : 'Clear'}
          </Button>
        </div>
        {originalInitialsSettingNotChanged ? (
          <div className={classes.image}>
            <img src={props.signer?.signingSettings?.initialsValue} alt="Saved initials" />
          </div>
        ) : (
          <CanvasWrapper
            ref={initialsRef}
            onEnd={() => updateValue(initialsRef.current!, 'initialsValue')}
            canvasProps={{ className: classes.canvas }}
            data-spec="initials-canvas"
            hasContent={hasInitials}
            key={1}
            canvasWidth={maxWidth}
            setMaxWidth={handleSetMaxWidth}
          />
        )}
      </div>
    </>
  );
};

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default DrawSignature;
